/**
 * Mapping from provider names and legacy aliases to display names.
 * Provider pages are advertised with lowercase slugs, but legacy encoded
 * display-name paths still resolve through this map.
 */
const PROVIDER_FOLDER_MAP: Record<string, string> = {
  'AWS Bedrock': 'AWS Bedrock',
  'Amazon': 'Amazon',
  'Anthropic': 'Anthropic',
  'Anthropic Direct': 'Anthropic Direct',
  'Anthropic API': 'Anthropic API',
  'Cohere': 'Cohere',
  'Cohere Platform': 'Cohere Platform',
  'DeepSeek': 'DeepSeek',
  'DeepSeek API': 'DeepSeek API',
  'Fireworks AI': 'Fireworks AI',
  'Fireworks API': 'Fireworks AI',
  'GCP Vertex AI': 'GCP Vertex AI',
  'Google': 'Google',
  'Meta': 'Meta',
  'MiniMax': 'MiniMax',
  'MiniMax API': 'MiniMax API',
  'Mistral AI': 'Mistral AI',
  'Mistral AI API': 'Mistral AI API',
  'Moonshot': 'Moonshot',
  'Moonshot API': 'Moonshot API',
  'NVIDIA': 'NVIDIA',
  'NVIDIA API': 'NVIDIA API',
  'OpenAI': 'OpenAI',
  'OpenAI API': 'OpenAI API',
  'Qwen': 'Qwen',
  'Qwen API': 'Qwen API',
  'Stability AI': 'Stability AI',
  'Stability AI API': 'Stability AI API',
  'Twelve Labs': 'Twelve Labs',
  'Writer': 'Writer',
  'AI21 Labs': 'AI21 Labs',
};

/**
 * Mapping from lowercase slugs to exact provider folder names
 * Used for URL lookup and matching
 */
const SLUG_TO_FOLDER_MAP: Record<string, string> = {
  'aws-bedrock': 'AWS Bedrock',
  'aws': 'AWS Bedrock',
  'amazon': 'Amazon',
  'anthropic': 'Anthropic',
  'anthropic-direct': 'Anthropic Direct',
  'anthropic-api': 'Anthropic API',
  'cohere': 'Cohere',
  'cohere-platform': 'Cohere Platform',
  'deepseek': 'DeepSeek',
  'deepseek-api': 'DeepSeek API',
  'fireworks-ai': 'Fireworks AI',
  'fireworks-api': 'Fireworks AI',
  'fireworks': 'Fireworks AI',
  'gcp-vertex-ai': 'GCP Vertex AI',
  'gcp': 'GCP Vertex AI',
  'google': 'Google',
  'meta': 'Meta',
  'minimax': 'MiniMax',
  'minimax-api': 'MiniMax API',
  'mistral-ai': 'Mistral AI',
  'mistral-ai-api': 'Mistral AI API',
  'mistral': 'Mistral AI',
  'moonshot': 'Moonshot',
  'moonshot-api': 'Moonshot API',
  'nvidia': 'NVIDIA',
  'nvidia-api': 'NVIDIA API',
  'openai': 'OpenAI',
  'openai-api': 'OpenAI API',
  'qwen': 'Qwen',
  'qwen-api': 'Qwen API',
  'stability-ai': 'Stability AI',
  'stability-ai-api': 'Stability AI API',
  'stability': 'Stability AI',
  'twelve-labs': 'Twelve Labs',
  'writer': 'Writer',
  'ai21-labs': 'AI21 Labs',
  'ai21': 'AI21 Labs',
};

function slugifyProviderName(providerName: string): string {
  return providerName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Generate a canonical URL-friendly slug from a provider name.
 * e.g., "AWS Bedrock" → "aws-bedrock"
 * e.g., "OpenAI API" → "openai-api"
 */
export function generateProviderSlug(providerName: string): string {
  if (PROVIDER_FOLDER_MAP[providerName]) {
    return slugifyProviderName(PROVIDER_FOLDER_MAP[providerName]);
  }
  
  // Try case-insensitive lookup
  const normalizedName = providerName.toLowerCase();
  for (const [key, value] of Object.entries(PROVIDER_FOLDER_MAP)) {
    if (key.toLowerCase() === normalizedName) {
      return slugifyProviderName(value);
    }
  }
  
  return slugifyProviderName(providerName);
}

/**
 * Map of known lowercase slugs to provider full names
 * This ensures consistent lookups
 */
const PROVIDER_SLUG_MAP: Record<string, string> = {
  'aws-bedrock': 'AWS Bedrock',
  'aws': 'AWS Bedrock',
  'amazon': 'Amazon',
  'anthropic': 'Anthropic',
  'anthropic-direct': 'Anthropic Direct',
  'anthropic-api': 'Anthropic API',
  'cohere': 'Cohere',
  'cohere-platform': 'Cohere Platform',
  'deepseek': 'DeepSeek',
  'deepseek-api': 'DeepSeek API',
  'fireworks-ai': 'Fireworks AI',
  'fireworks-api': 'Fireworks AI',
  'fireworks': 'Fireworks AI',
  'gcp-vertex-ai': 'GCP Vertex AI',
  'gcp': 'GCP Vertex AI',
  'google': 'Google',
  'meta': 'Meta',
  'minimax': 'MiniMax',
  'minimax-api': 'MiniMax API',
  'mistral-ai': 'Mistral AI',
  'mistral-ai-api': 'Mistral AI API',
  'mistral': 'Mistral AI',
  'moonshot': 'Moonshot',
  'moonshot-api': 'Moonshot API',
  'nvidia': 'NVIDIA',
  'nvidia-api': 'NVIDIA API',
  'openai': 'OpenAI',
  'openai-api': 'OpenAI API',
  'qwen': 'Qwen',
  'qwen-api': 'Qwen API',
  'stability-ai': 'Stability AI',
  'stability-ai-api': 'Stability AI API',
  'stability': 'Stability AI',
  'twelve-labs': 'Twelve Labs',
  'writer': 'Writer',
  'ai21-labs': 'AI21 Labs',
  'ai21': 'AI21 Labs',
};

/**
 * Convert a slug back to provider name
 * Uses known mappings first, then tries to reconstruct
 */
export function slugToProviderName(slug: string): string {
  // Decode URL-encoded slug first (e.g., "OpenAI%20API" → "OpenAI API")
  const decodedSlug = decodeURIComponent(slug);
  
  // Check if it directly matches a folder name
  for (const folderName of Object.values(PROVIDER_FOLDER_MAP)) {
    if (folderName.toLowerCase() === decodedSlug.toLowerCase()) {
      return folderName;
    }
  }
  
  // Check known slug mappings
  const lowerSlug = decodedSlug.toLowerCase();
  if (PROVIDER_SLUG_MAP[lowerSlug]) {
    return PROVIDER_SLUG_MAP[lowerSlug];
  }
  if (SLUG_TO_FOLDER_MAP[lowerSlug]) {
    return SLUG_TO_FOLDER_MAP[lowerSlug];
  }
  
  // Try to reconstruct: aws-bedrock → Aws Bedrock
  return decodedSlug
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Get all known provider names
 */
export function getKnownProviderNames(): string[] {
  return [...new Set(Object.values(SLUG_TO_FOLDER_MAP))];
}

/**
 * Find provider name from slug, handling both canonical lowercase slugs and
 * legacy encoded display-name URLs.
 */
export function findProviderBySlug(slug: string): string | null {
  // Decode URL-encoded slug
  const decodedSlug = decodeURIComponent(slug);
  const lowerSlug = decodedSlug.toLowerCase();
  
  // Check exact legacy display-name match first.
  for (const folderName of Object.values(PROVIDER_FOLDER_MAP)) {
    if (folderName.toLowerCase() === lowerSlug.replace(/-/g, ' ')) {
      return folderName;
    }
  }
  
  // Check slug mappings
  if (SLUG_TO_FOLDER_MAP[lowerSlug]) {
    return SLUG_TO_FOLDER_MAP[lowerSlug];
  }
  
  if (PROVIDER_SLUG_MAP[lowerSlug]) {
    return PROVIDER_SLUG_MAP[lowerSlug];
  }
  
  // Try normalized comparison (handle both "anthropic-direct" and "anthropic direct")
  const normalizedSlug = lowerSlug.replace(/-/g, ' ');
  for (const folderName of Object.values(PROVIDER_FOLDER_MAP)) {
    if (folderName.toLowerCase() === normalizedSlug) {
      return folderName;
    }
  }
  
  return null;
}

/**
 * Get the display name for a provider or legacy alias.
 */
export function getProviderFolderName(providerName: string): string | null {
  // Direct lookup
  if (PROVIDER_FOLDER_MAP[providerName]) {
    return PROVIDER_FOLDER_MAP[providerName];
  }
  
  // Case-insensitive lookup
  const lowerName = providerName.toLowerCase();
  for (const [key, value] of Object.entries(PROVIDER_FOLDER_MAP)) {
    if (key.toLowerCase() === lowerName) {
      return value;
    }
  }
  
  return null;
}

/**
 * Create a provider link using the canonical lowercase slug.
 */
export function getProviderLink(providerName: string): string {
  const folderName = getProviderFolderName(providerName);
  if (folderName) {
    return `/providers/${generateProviderSlug(folderName)}`;
  }
  
  const slug = generateProviderSlug(providerName);
  return `/providers/${slug}`;
}
